import { platformApi } from '@/apis';
import { featureSortFn, filterType, reviveDate } from '@/utils';
import { captureMessage } from '@sentry/react';
import { parseServerSentEvents } from 'parse-sse';
import { useState } from 'react';

export function useStreamingVehiclesFeatureCollection(
  search,
  sort,
  filter,
  enabled = true,
) {
  const [streamStarted, setStreamStarted] = useState(false);
  const [identificationNumberLookup, setIdentificationNumberLookup] = useState(
    {},
  );
  const [imeiLookup, setImeiLookup] = useState({});
  const [featureCollection, setFeatureCollection] = useState({
    type: 'FeatureCollection',
    features: [],
  });
  const ctrl = new AbortController();

  async function initiateStreams() {
    if (streamStarted) {
      return;
    }

    setStreamStarted(true);

    try {
      const trackerValuesSource = platformApi.get('sse/tracker-values', {
        signal: ctrl.signal,
        headers: { accept: 'text/event-stream' },
      });
      const vehiclesSource = platformApi.get('sse/vehicles', {
        signal: ctrl.signal,
        headers: { accept: 'text/event-stream' },
      });
      const [trackerValues, vehicles] = await Promise.all([
        trackerValuesSource,
        vehiclesSource,
      ]);

      await Promise.all([
        (async () => {
          for await (const msg of parseServerSentEvents(trackerValues)) {
            if (msg.data) {
              const newFeature = JSON.parse(msg.data, reviveDate);

              const vehicle =
                identificationNumberLookup[
                  newFeature.properties.vehicleIdentificationNumber
                ] || imeiLookup[newFeature.properties.imei];

              if (vehicle) {
                newFeature.properties.vehicle = vehicle;
              }

              setFeatureCollection((featureCollection) => {
                const alreadyExists = featureCollection.features.some(
                  (feature) => feature.id === newFeature.id,
                );

                if (alreadyExists && msg.event === 'initial') {
                  return featureCollection;
                }

                return {
                  ...featureCollection,
                  features: alreadyExists
                    ? featureCollection.features.map((feature) =>
                        feature.id === newFeature.id ? newFeature : feature,
                      )
                    : [...featureCollection.features, newFeature],
                };
              });
            }
          }
        })(),
        (async () => {
          for await (const msg of parseServerSentEvents(vehicles)) {
            if (msg.data) {
              const vehicle = JSON.parse(msg.data);

              setIdentificationNumberLookup((lookup) => ({
                ...lookup,
                [vehicle.identificationNumber]: vehicle,
              }));

              if (vehicle.telematicsBoxImei) {
                setImeiLookup((lookup) => ({
                  ...lookup,
                  [vehicle.telematicsBoxImei]: vehicle,
                }));
              }

              setFeatureCollection((featureCollection) => ({
                ...featureCollection,
                features: featureCollection.features.map((feature) =>
                  feature.properties.vehicleIdentificationNumber ===
                    vehicle.identificationNumber ||
                  feature.properties.imei === vehicle.telematicsBoxImei
                    ? {
                        ...feature,
                        properties: {
                          ...feature.properties,
                          vehicle,
                        },
                      }
                    : feature,
                ),
              }));
            }
          }
        })(),
      ]);
    } catch (error) {
      if (error.name === 'AbortError') {
        captureMessage('Stream aborted', { level: 'debug' });
      } else {
        throw error;
      }
    }
  }

  if (enabled) {
    initiateStreams();
  }

  return {
    ...featureCollection,
    features: featureCollection.features
      .filter(({ properties }) => filterType('vehicles', filter, properties))
      .filter(({ properties }) =>
        JSON.stringify(properties).toLowerCase().includes(search.toLowerCase()),
      )
      .sort(featureSortFn(sort)),
    total: featureCollection.features.length,
  };
}
